import React, { useEffect, useRef, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination } from "swiper/modules";
import { ReactComponent as ImageExpandIcon } from "assets/icons/image_expand.svg";
import { ReactComponent as ArrowRightIcon } from "assets/icons/slider_arrow_right_2.svg";
import { ReactComponent as DisableCameraIcon } from "assets/icons/disable_camera.svg";
import { ReactComponent as LocationPinIcon } from "assets/icons/location_pin_blue.svg";
import CardImageViewerSlider from "./CardImageViewerSlider";
import { ReactComponent as EditIcon } from "assets/icons/edit_pencil.svg";
import useBookmark from "hooks/useBookmark";
import { motion } from "framer-motion";
import IdentifyPortal from "./IdentifyPortal";
import useProperties from "hooks/useProperties";

const SearchResultPropertyCardImageSlider = ({
  property,
  isOnMapMini = false,
  isOrganisePage,
  isBookmarked,
  isBookmarkPropertyHasNote,
}) => {
  const [isImageViewerOpen, setIsImageViewerOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [imageHasError, setImageHasError] = useState(false);
  const { togglePropertyNoteDialog } = useBookmark();
  const [isPlatformDropdownOpen, setIsPlatformDropdownOpen] = useState(false);
  const { activatePropertyOnMap, activeMapProperty } = useProperties();

  const platformDropdownRef = useRef(null);
  const buttonRef = useRef(null);

  const settings = {
    modules: [Navigation, Pagination],
    slidesPerView: 1,
    navigation: {
      nextEl: `.swiper-${isOnMapMini ? "map-" : "main-"}${
        property.property_id
      }-next`,
      prevEl: `.swiper-${isOnMapMini ? "map-" : "main-"}${
        property.property_id
      }-prev`,
    },
    pagination: !isOnMapMini
      ? {
          clickable: true,
          dynamicBullets: true,
          renderBullet: (index, className) => {
            return `<span class="${className} property_card_swiper_dots">${
              index + 1
            }</span>`;
          },
        }
      : false,
    onSlideChange: (swiper) => {
      setCurrentIndex(swiper.activeIndex);
    },
  };

  const totalSlides = property?.pictures?.length || 0;
  const filteredListings =
    property?.listings?.filter((listing) => listing?.sale_price > 0) || [];

  // Toggle dropdown and set its position
  const togglePlatformDropdown = () => {
    setIsPlatformDropdownOpen((prev) => !prev);
  };

  // Location pin click to open the marker on map
  const handleLocationPinClick = (e) => {
    e.stopPropagation();

    if (property?._id) {
      if (activeMapProperty === property._id) {
        activatePropertyOnMap(null);
        setTimeout(() => activatePropertyOnMap(property._id), 0);
      } else {
        activatePropertyOnMap(property._id);
      }
    }
  };

  // Close platform dropdown when clicking outside
  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (
        platformDropdownRef.current &&
        !platformDropdownRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target)
      ) {
        setIsPlatformDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  return (
    <div className="relative">
      {imageHasError ? (
        <div className="w-full bg-[#F9F6F6] object-cover flex items-center justify-center rounded-tl-[8px] rounded-tr-[8px] h-[177px]">
          <DisableCameraIcon />
        </div>
      ) : (
        <>
          <Swiper {...settings}>
            {property?.pictures?.length > 0 &&
              property.pictures.map((image, ind) => (
                <SwiperSlide key={ind}>
                  <img
                    className="w-full object-cover rounded-tl-[8px] rounded-tr-[8px] h-[177px]"
                    src={image}
                    alt="property"
                    onError={() => setImageHasError(true)}
                  />
                </SwiperSlide>
              ))}
          </Swiper>
          <div
            style={{ zIndex: isPlatformDropdownOpen ? 3 : 1 }}
            className="absolute top-0 left-0 w-full h-full"
          >
            <div
              ref={buttonRef}
              onClick={(e) => {
                e.stopPropagation();
                togglePlatformDropdown();
              }}
              className="bg-[#0A0A14] mt-3 ml-[14.5px] bg-opacity-70 max-w-fit rounded-[32px] h-[24px] cursor-pointer  py-1 px-2"
            >
              <p className="text-[12px] leading-[16px] font-medium text-white text-center">
                Trovata su {filteredListings.length}
                {filteredListings?.length > 1 ? " Piattaforme" : " Piattaforma"}
              </p>
            </div>
            {isPlatformDropdownOpen && (
              <motion.div
                onClick={(e) => e.stopPropagation()}
                ref={platformDropdownRef}
                initial={{ opacity: 0, y: -10, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -10, scale: 0.95 }}
                transition={{ duration: 0.2, ease: "easeInOut" }}
                className="absolute bg-white top-[38px] left-[14.5px] shadow-lg rounded-lg p-3 z-[2] w-[320px] "
              >
                <IdentifyPortal property={property} height={"auto"} />
              </motion.div>
            )}
          </div>
          <div
            onClick={handleLocationPinClick}
            className="absolute right-3 top-[10px] z-[1]"
          >
            <LocationPinIcon className="w-[18px] h-[24px] cursor-pointer" />
          </div>

          {isBookmarked &&
            ((!isOrganisePage && isBookmarkPropertyHasNote) ||
            isOrganisePage ? (
              <div
                onClick={(e) => {
                  e.stopPropagation();
                  togglePropertyNoteDialog(property?._id);
                }}
                className="w-6 h-6 absolute right-[40px] z-[1] top-[10px] rounded-[5px] bg-white cursor-pointer note-btn-shadow flex items-center justify-center"
              >
                <EditIcon
                  className={`${
                    isBookmarkPropertyHasNote
                      ? "text-primary"
                      : "text-[#0000004D]"
                  }`}
                />
              </div>
            ) : null)}

          {isOnMapMini && (
            <div className="bg-[#D9D9D933] rounded-[4px] text-[10px] text-white font-medium z-[1] absolute top-[9px] left-[9px] border border-white p-1">
              {currentIndex + 1}/{totalSlides}
            </div>
          )}

          {isImageViewerOpen && (
            <CardImageViewerSlider
              setIsImageViewerOpen={setIsImageViewerOpen}
              pictures={property?.pictures}
            />
          )}

          {/* Custom Navigation Arrows */}
          <div
            onClick={(e) => e.stopPropagation()}
            className={`swiper-${isOnMapMini ? "map-" : "main-"}${
              property.property_id
            }-next absolute right-[17px] top-1/2 z-[1] -translate-y-1/2 cursor-pointer w-9 h-9 rounded-[46px] swiper-arrow-bg flex items-center justify-center`}
          >
            <ArrowRightIcon fill="white" width={16} height={16} />
          </div>
          <div
            onClick={(e) => e.stopPropagation()}
            className={`swiper-${isOnMapMini ? "map-" : "main-"}${
              property.property_id
            }-prev absolute left-[17px] top-1/2 z-[1] -translate-y-1/2 cursor-pointer rotate-180 w-9 h-9 rounded-[46px] swiper-arrow-bg flex items-center justify-center`}
          >
            <ArrowRightIcon fill="white" width={16} height={16} />
          </div>
        </>
      )}
    </div>
  );
};

export default SearchResultPropertyCardImageSlider;
